import { useState } from "react";
import type { OpsDealTypeSlot } from "@/lib/opsDealTypesClient";
import {
  OPS_DEAL_TYPE_SLOT_KINDS,
  slotKindLabel,
  type OpsDealTypeSlotKind,
} from "@/lib/opsDealTypes/opsDealTypeKeys";
import { DEFAULT_PLANT_PACKET_SLOTS } from "@/lib/ops/plantPacket";

export type DealTypesAddSlotInput = {
  label: string;
  slot_kind: OpsDealTypeSlotKind;
  packet_layer?: "ops" | "plant";
  required?: boolean;
  slot_key?: string;
};

export function DealTypesProductSlots(props: {
  slots: OpsDealTypeSlot[];
  ownerLabel: string;
  allowPlantLayer?: boolean;
  onAddSlot: (input: DealTypesAddSlotInput) => Promise<void>;
  onRemoveSlot: (id: string) => Promise<void>;
}) {
  const [slotLabel, setSlotLabel] = useState("");
  const [slotKind, setSlotKind] = useState<OpsDealTypeSlotKind>("required_upload");
  const [packetLayer, setPacketLayer] = useState<"ops" | "plant">("ops");
  const [required, setRequired] = useState(true);
  const plantLayer = props.allowPlantLayer === true;

  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Paperwork blocks</h4>
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        {props.ownerLabel}{" "}
        {plantLayer
          ? "Ops slots stay on this selling org. Plant slots are what dealers must send this manufacturer — they appear on retail deals for buildings this plant builds."
          : "Give (no signed copy back), Sign-out (expect signed return), Return upload. Product paperwork plus sale paperwork combine when a deal is instantiated."}
      </p>
      <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-zinc-700 dark:border-zinc-700">
        {props.slots.length === 0 ? (
          <li className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-400">No slots yet.</li>
        ) : (
          props.slots.map((slot) => (
            <li key={slot.id} className="flex items-start justify-between gap-2 px-3 py-2 text-sm">
              <div>
                <div className="font-medium text-gray-900 dark:text-zinc-100">{slot.label}</div>
                <div className="text-xs text-gray-500 dark:text-zinc-400">
                  {slot.packet_layer === "plant" ? "Plant · " : "Ops · "}
                  {slotKindLabel(slot.slot_kind)}
                  {slot.required ? " · required" : " · optional"}
                </div>
              </div>
              <button
                type="button"
                className="text-xs text-red-600 dark:text-red-400"
                onClick={() => void props.onRemoveSlot(slot.id)}
              >
                Remove
              </button>
            </li>
          ))
        )}
      </ul>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm">
          New slot label
          <input
            className="mt-1 block rounded border border-gray-300 px-2 py-1 dark:border-zinc-600 dark:bg-zinc-900"
            value={slotLabel}
            onChange={(e) => setSlotLabel(e.target.value)}
          />
        </label>
        {plantLayer ? (
          <label className="text-sm">
            Packet
            <select
              className="mt-1 block rounded border border-gray-300 px-2 py-1 dark:border-zinc-600 dark:bg-zinc-900"
              value={packetLayer}
              onChange={(e) => setPacketLayer(e.target.value as "ops" | "plant")}
            >
              <option value="ops">Ops (this org’s deal paperwork)</option>
              <option value="plant">Plant (dealers send the factory)</option>
            </select>
          </label>
        ) : null}
        <label className="text-sm">
          Kind
          <select
            className="mt-1 block rounded border border-gray-300 px-2 py-1 dark:border-zinc-600 dark:bg-zinc-900"
            value={slotKind}
            onChange={(e) => setSlotKind(e.target.value as OpsDealTypeSlotKind)}
          >
            {OPS_DEAL_TYPE_SLOT_KINDS.map((k) => (
              <option key={k} value={k}>
                {slotKindLabel(k)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 pb-1 text-sm">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          Required
        </label>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600"
          onClick={() => {
            if (!slotLabel.trim()) return;
            void props
              .onAddSlot({
                label: slotLabel.trim(),
                slot_kind: slotKind,
                packet_layer: plantLayer ? packetLayer : "ops",
                required,
              })
              .then(() => setSlotLabel(""));
          }}
        >
          Add slot
        </button>
        {plantLayer ? (
          <button
            type="button"
            className="rounded-md border border-indigo-300 px-3 py-1.5 text-sm text-indigo-800 dark:border-indigo-800 dark:text-indigo-200"
            onClick={() => {
              void (async () => {
                const have = new Set(props.slots.filter((s) => s.packet_layer === "plant").map((s) => s.slot_key));
                for (const slot of DEFAULT_PLANT_PACKET_SLOTS) {
                  if (have.has(slot.item_key)) continue;
                  await props.onAddSlot({
                    label: slot.label,
                    slot_kind: "required_upload",
                    packet_layer: "plant",
                    required: slot.required,
                    slot_key: slot.item_key,
                  });
                }
              })();
            }}
          >
            Add default plant packet
          </button>
        ) : null}
      </div>
    </section>
  );
}
